import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { 
    fetchPickingProductivitiesInsights, 
    fetchPickingProductivitiesStatsByPicker, 
    fetchPickingProductivitiesHourlyStats, 
    fetchPickingProductivitiesIntervalStats 
} from '../../services';
import { Button, Loader, PageTopBar, PageView } from '../../components';

const VIEW_MODES = {
    DAY: 'day',
    HOUR: 'hour',
    INTERVAL: 'interval'
};

export const PickingProductivitiesContainer = () => {
    const { warehouse_id } = useParams();
    const { t } = useTranslation();
    const [viewMode, setViewMode] = useState(VIEW_MODES.DAY);
    const [emailList, setEmailList] = useState('');
    const [customMetrics, setCustomMetrics] = useState([]);
    const [teamMetrics, setTeamMetrics] = useState({
        totalPickers: 0,
        avgItemsToday: 0,
        avgItems3Days: 0,
        avgProductivity: 0,
        pickerDetails: []
    });

    // Fetch insights data
    const {
        data: insightsData,
        isLoading: isLoadingInsights,
        isError: isErrorInsights,
        error: insightsError,
        refetch: refetchInsights
    } = useQuery(
        ['picking-productivities-insights', warehouse_id],
        () => fetchPickingProductivitiesInsights(warehouse_id),
        {
            refetchInterval: 60000, // Refresh every minute
            keepPreviousData: true
        }
    );

    // Fetch picker stats
    const {
        data: pickerStatsData,
        isLoading: isLoadingPickerStats,
        isError: isErrorPickerStats,
        error: pickerStatsError,
        refetch: refetchPickerStats
    } = useQuery(
        ['picking-productivities-picker-stats', warehouse_id],
        () => fetchPickingProductivitiesStatsByPicker(warehouse_id),
        {
            refetchInterval: 60000,
            keepPreviousData: true
        }
    );

    // Fetch hourly stats
    const {
        data: hourlyStatsData,
        isLoading: isLoadingHourlyStats,
        isError: isErrorHourlyStats,
        error: hourlyStatsError,
        refetch: refetchHourlyStats
    } = useQuery(
        ['picking-productivities-hourly-stats', warehouse_id],
        () => fetchPickingProductivitiesHourlyStats(warehouse_id),
        {
            refetchInterval: 60000,
            keepPreviousData: true,
            staleTime: 30000, // Keep data fresh for 30 seconds
            cacheTime: 300000 // Cache for 5 minutes
        }
    );

    // Fetch interval stats
    const {
        data: intervalStatsData,
        isLoading: isLoadingIntervalStats,
        isError: isErrorIntervalStats,
        error: intervalStatsError,
        refetch: refetchIntervalStats
    } = useQuery(
        ['picking-productivities-interval-stats', warehouse_id],
        () => fetchPickingProductivitiesIntervalStats(warehouse_id),
        {
            refetchInterval: 60000,
            keepPreviousData: true,
            staleTime: 30000, // Keep data fresh for 30 seconds
            cacheTime: 300000 // Cache for 5 minutes
        }
    );

    // Manual refresh function
    const handleRefresh = () => {
        refetchInsights();
        refetchPickerStats();
        refetchHourlyStats();
        refetchIntervalStats();
    };

    // Convert UTC hour to BR/SP timezone (UTC-3)
    const convertToBRTime = (utcHour) => {
        if (warehouse_id === '232') {
            try {
                // Ensure utcHour is in HH:MM format
                if (!utcHour || typeof utcHour !== 'string') {
                    console.warn('Invalid utcHour:', utcHour);
                    return utcHour;
                }
                
                // If already in HH:MM format, just subtract 3 hours
                if (utcHour.includes(':')) {
                    const [hours, minutes] = utcHour.split(':').map(Number);
                    let newHours = hours - 3;
                    
                    // Handle negative hours (wrap to previous day)
                    if (newHours < 0) {
                        newHours += 24;
                    }
                    
                    return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                }
                
                // Fallback to Date object method
                const utcDate = new Date(`2000-01-01T${utcHour}:00:00Z`);
                if (isNaN(utcDate.getTime())) {
                    console.warn('Invalid date created from utcHour:', utcHour);
                    return utcHour;
                }
                
                const brDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
                return brDate.toTimeString().slice(0, 5); // Returns "HH:MM"
            } catch (error) {
                console.error('Error converting time:', error, 'utcHour:', utcHour);
                return utcHour;
            }
        }
        return utcHour; // Return original hour for other warehouses
    };

    // Format hour display based on warehouse
    const formatHourDisplay = (hour) => {
        if (warehouse_id === '232') {
            return `${hour} (BR)`;
        }
        return hour;
    };

    // Calculate working days (excluding Sundays) since picker creation
    const calculateWorkingDays = (createdAt) => {
        if (!createdAt) return 0;
        
        try {
            const createdDate = new Date(createdAt);
            const today = new Date();
            
            // Set both dates to start of day for accurate calculation
            createdDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            
            let workingDays = 0;
            const currentDate = new Date(createdDate);
            
            while (currentDate <= today) {
                // Check if it's not a Sunday (0 = Sunday, 1-6 = Monday-Saturday)
                if (currentDate.getDay() !== 0) {
                    workingDays++;
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
            
            return workingDays;
        } catch (error) {
            console.error('Error calculating working days:', error);
            return 0;
        }
    };

    // Check if this cell should have a left border (start of new day)
    const shouldHaveLeftBorder = (currentInterval, allIntervals, currentIndex) => {
        if (viewMode !== VIEW_MODES.HOUR || !currentInterval.includes('|')) return false;
        
        // First cell always has border
        if (currentIndex === 0) return true;
        
        // Check if previous cell is from a different day
        const [, currentDay] = currentInterval.split('|');
        const previousInterval = allIntervals[currentIndex - 1];
        
        if (previousInterval && previousInterval.includes('|')) {
            const [, prevDay] = previousInterval.split('|');
            return currentDay !== prevDay;
        }
        
        return false;
    };

    // Format hour-day display for table headers
    const formatHourDayDisplay = (interval) => {
        if (viewMode === VIEW_MODES.HOUR && interval.includes('|')) {
            const [hour, day] = interval.split('|');
            return (
                <div className="text-center">
                    <div className="font-medium">{hour}</div>
                    <div className="text-xs text-gray-500">Day {day}</div>
                </div>
            );
        } else if (viewMode === VIEW_MODES.DAY && interval.includes('|')) {
            const [dayName, dayNumber] = interval.split('|');
            return (
                <div className="text-center">
                    <div className="font-medium">{dayName}</div>
                    <div className="text-xs text-gray-500">Day {dayNumber}</div>
                </div>
            );
        }
        return interval;
    };

    const isLoading = isLoadingInsights || isLoadingPickerStats || 
                    (viewMode === VIEW_MODES.HOUR && isLoadingHourlyStats) ||
                    (viewMode === VIEW_MODES.INTERVAL && isLoadingIntervalStats);

    const hasError = isErrorInsights || isErrorPickerStats || 
                    (viewMode === VIEW_MODES.HOUR && isErrorHourlyStats) ||
                    (viewMode === VIEW_MODES.INTERVAL && isErrorIntervalStats);

    const errorMessage = insightsError?.message || pickerStatsError?.message || 
                        hourlyStatsError?.message || intervalStatsError?.message;

    // Check if we have the minimum required data to render
    const hasMinimumData = pickerStatsData?.picking_productivities && 
                          pickerStatsData.picking_productivities.length > 0;

    // More flexible data validation
    const hasAnyData = (pickerStatsData && Object.keys(pickerStatsData).length > 0) ||
                      (hourlyStatsData && Object.keys(hourlyStatsData).length > 0) ||
                      (intervalStatsData && Object.keys(intervalStatsData).length > 0);

    // Check for different possible data structures
    const hasPickerData = pickerStatsData?.picking_productivities?.length > 0 ||
                         pickerStatsData?.data?.length > 0 ||
                         pickerStatsData?.length > 0;

    // Calculate team metrics when picker stats data is available
    useEffect(() => {
        if (pickerStatsData?.picking_productivities) {
            calculateTeamMetrics();
        }
    }, [pickerStatsData]);



    // Helper functions for heatmap table
    const getTimeIntervals = () => {
        if (viewMode === VIEW_MODES.DAY) {
            // Show past 3 days with consistent format
            const days = [];
            const today = new Date();
            for (let i = 2; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                const dayName = date.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                });
                // Create a consistent key format: "Mon, Jan 1|30" where 30 is the day number
                const dayNumber = date.getDate();
                const dayKey = `${dayName}|${dayNumber}`;
                days.push(dayKey);
            }
            return days;
        } else if (viewMode === VIEW_MODES.HOUR) {
            
            if (!insightsData?.stats_24h_by_hour) {
                return [];
            }
            
            // Get all unique hours from the stats_24h_by_hour data
            const allHours = new Set();
            insightsData.stats_24h_by_hour.forEach((picker, index) => {
                if (picker.hours) {
                    Object.keys(picker.hours).forEach(hourKey => {
                        const hourData = picker.hours[hourKey];
                        
                        // hourData.hour already contains just the time part (e.g., "11:00")
                        const utcHour = hourData.hour;
                        
                        const localHour = convertToBRTime(utcHour);
                        
                        // Extract day from hourKey (e.g., "2025-08-30 11:00" -> "30")
                        const dayMatch = hourKey.match(/\d{4}-\d{2}-(\d{2})/);
                        let day = dayMatch ? dayMatch[1] : '';
                        
                        // If we're converting to BR time and the hour crosses midnight, adjust the day
                        if (warehouse_id === '232' && dayMatch) {
                            const [hours] = utcHour.split(':').map(Number);
                            let newHours = hours - 3;
                            
                            // If the converted hour is negative, it means we crossed to the previous day
                            if (newHours < 0) {
                                // Parse the date from hourKey (format: "2025-09-02 01:00")
                                const [datePart, timePart] = hourKey.split(' ');
                                const originalDate = new Date(datePart + 'T' + timePart + ':00Z');
                                const previousDay = new Date(originalDate.getTime() - (24 * 60 * 60 * 1000));
                                day = previousDay.getDate().toString().padStart(2, '0');
                            }
                        }
                        
                        // Create combined key with hour and day
                        const hourDayKey = `${localHour}|${day}`;
                        allHours.add(hourDayKey);
                    });
                } else {
                }
            });
            
            // Convert to array and sort chronologically
            const sortedHours = Array.from(allHours).sort((a, b) => {
                const [hourA, dayA] = a.split('|');
                const [hourB, dayB] = b.split('|');
                
                // First sort by day, then by hour
                if (dayA !== dayB) {
                    return parseInt(dayA) - parseInt(dayB);
                }
                
                const timeA = new Date(`2000-01-01 ${hourA}`);
                const timeB = new Date(`2000-01-01 ${hourB}`);
                return timeA - timeB;
            });
            
            return sortedHours;
        } else if (viewMode === VIEW_MODES.INTERVAL) {
            if (!insightsData?.stats_24h_by_interval) return [];
            
            // Get all unique intervals from the stats_24h_by_interval data
            const allIntervals = new Set();
            insightsData.stats_24h_by_interval.forEach(picker => {
                if (picker.intervals) {
                    Object.keys(picker.intervals).forEach(intervalKey => {
                        const intervalData = picker.intervals[intervalKey];
                        // Extract just the time part (e.g., "16:00" from "2024-01-01 16:00")
                        const utcTime = intervalData.time.split(' ')[1];
                        const localTime = convertToBRTime(utcTime);
                        allIntervals.add(localTime);
                    });
                }
            });
            
            // Convert to array and sort chronologically
            const sortedIntervals = Array.from(allIntervals).sort((a, b) => {
                const timeA = new Date(`2000-01-01 ${a}`);
                const timeB = new Date(`2000-01-01 ${b}`);
                return timeA - timeB;
            });
            
            return sortedIntervals;
        }
        return [];
    };

    const getHeatmapData = () => {
        // Try different possible data structures
        let data = null;
        
        if (pickerStatsData?.picking_productivities) {
            data = pickerStatsData.picking_productivities;
        } else if (pickerStatsData?.data) {
            data = pickerStatsData.data;
        } else if (Array.isArray(pickerStatsData)) {
            data = pickerStatsData;
        }
        
        if (!data || data.length === 0) {
            return [];
        }
        
        return data
            .filter(picker => picker.total_items_24h > 0) // Only show active pickers
            .sort((a, b) => b.total_items_24h - a.total_items_24h);
    };

    const getPickerValueForInterval = (picker, interval) => {
        if (viewMode === VIEW_MODES.DAY) {
            // Extract actual daily data from hourly stats
            if (!insightsData?.stats_24h_by_hour) return 0;
            
            // Find the picker in hourly stats data
            const pickerHourlyData = insightsData.stats_24h_by_hour.find(p => p.email === picker.email);
            if (!pickerHourlyData?.hours) return 0;
            
            // Parse the interval to get the day number (e.g., "Mon, Jan 1|30" -> extract "30")
            const dayMatch = interval.match(/\|(\d+)$/);
            if (!dayMatch) return 0;
            
            const targetDay = parseInt(dayMatch[1]);
            
            // Sum up all items picked for this specific day
            let dailyTotal = 0;
            Object.keys(pickerHourlyData.hours).forEach(hourKey => {
                const hourData = pickerHourlyData.hours[hourKey];
                
                // Extract day from hourKey (e.g., "2025-08-30 11:00" -> "30")
                const hourDayMatch = hourKey.match(/\d{4}-\d{2}-(\d{2})/);
                if (hourDayMatch) {
                    const hourDay = parseInt(hourDayMatch[1]);
                    if (hourDay === targetDay) {
                        dailyTotal += hourData.items_picked || 0;
                    }
                }
            });
            
            return dailyTotal;
        } else if (viewMode === VIEW_MODES.HOUR) {
            if (!insightsData?.stats_24h_by_hour) return 0;
            
            // Find the picker in hourly stats data
            const pickerHourlyData = insightsData.stats_24h_by_hour.find(p => p.email === picker.email);

            if (!pickerHourlyData?.hours) {
                return 0;
            }
            
            // Find the hour that matches this interval
            let foundValue = 0;
            Object.keys(pickerHourlyData.hours).forEach(hourKey => {
                const hourData = pickerHourlyData.hours[hourKey];
                const utcHour = hourData.hour; // hourData.hour already contains just the time part
                const localHour = convertToBRTime(utcHour);
                // Extract day from hourKey
                const dayMatch = hourKey.match(/\d{4}-\d{2}-(\d{2})/);
                let day = dayMatch ? dayMatch[1] : '';
                
                // If we're converting to BR time and the hour crosses midnight, adjust the day
                if (warehouse_id === '232' && dayMatch) {
                    const [hours] = utcHour.split(':').map(Number);
                    let newHours = hours - 3;
                    
                    // If the converted hour is negative, it means we crossed to the previous day
                    if (newHours < 0) {
                        // Parse the date from hourKey (format: "2025-09-02 01:00")
                        const [datePart, timePart] = hourKey.split(' ');
                        const originalDate = new Date(datePart + 'T' + timePart + ':00Z');
                        const previousDay = new Date(originalDate.getTime() - (24 * 60 * 60 * 1000));
                        day = previousDay.getDate().toString().padStart(2, '0');
                    }
                }
                
                // Create the same format as used in getTimeIntervals
                const hourDayKey = `${localHour}|${day}`;
            
                if (hourDayKey === interval) {
                    foundValue = hourData.items_picked || 0;
                }
            });
            
            return foundValue;
        } else if (viewMode === VIEW_MODES.INTERVAL) {
            if (!insightsData?.stats_24h_by_interval) return 0;
            
            // Find the picker in interval stats data
            const pickerIntervalData = insightsData.stats_24h_by_interval.find(p => p.email === picker.email);
            if (!pickerIntervalData?.intervals) return 0;
            
            // Find the interval that matches this time
            let foundValue = 0;
            Object.keys(pickerIntervalData.intervals).forEach(intervalKey => {
                const intervalData = pickerIntervalData.intervals[intervalKey];
                const utcTime = intervalData.time.split(' ')[1];
                const localTime = convertToBRTime(utcTime);
                if (localTime === interval) {
                    foundValue = intervalData.items_picked || 0;
                }
            });
            
            return foundValue;
        }
        return 0;
    };

    const getCellColor = (value) => {
        if (!value || value === 0) {
            return 'bg-gray-100 text-gray-500';
        }
        
        // For hourly view: 220+ items = green, 100-219 = yellow, <100 = red
        // For daily view: 2200+ items = green, 1000-2199 = yellow, <1000 = red
        // For interval view: 22+ items = green, 10-21 = yellow, <10 = red
        
        let threshold;
        if (viewMode === VIEW_MODES.DAY) {
            threshold = 2200; // Daily threshold
        } else if (viewMode === VIEW_MODES.HOUR) {
            threshold = 220; // Hourly threshold
        } else {
            threshold = 22; // 10-minute interval threshold
        }
        
        if (value >= threshold) {
            return 'bg-green-100 text-green-800 border border-green-200';
        } else if (value >= threshold * 0.45) { // 45% of threshold for yellow
            return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
        } else {
            return 'bg-red-100 text-red-800 border border-red-200';
        }
    };

    const handleGetMetrics = () => {
        if (!emailList.trim()) return;
        
        const emails = emailList.trim().split('\n').filter(email => email.trim());
        const metrics = [];
        
        emails.forEach(email => {
            const trimmedEmail = email.trim();
            
            // Find picker data for today
            const todayData = pickerStatsData?.picking_productivities?.find(p => p.email === trimmedEmail);
            
            if (todayData) {
                // Calculate 3-day average from hourly stats
                let avg3Days = 0;
                let dayCount = 0;
                
                if (insightsData?.stats_24h_by_hour) {
                    const pickerHourlyData = insightsData.stats_24h_by_hour.find(p => p.email === trimmedEmail);
                    if (pickerHourlyData?.hours) {
                        const dailyTotals = {};
                        
                        // Group items by day
                        Object.keys(pickerHourlyData.hours).forEach(hourKey => {
                            const hourData = pickerHourlyData.hours[hourKey];
                            const dayMatch = hourKey.match(/\d{4}-\d{2}-(\d{2})/);
                            if (dayMatch) {
                                const day = dayMatch[1];
                                dailyTotals[day] = (dailyTotals[day] || 0) + (hourData.items_picked || 0);
                            }
                        });
                        
                        // Calculate average of last 3 days
                        const days = Object.values(dailyTotals);
                        if (days.length > 0) {
                            avg3Days = days.reduce((sum, val) => sum + val, 0) / days.length;
                            dayCount = days.length;
                        }
                    }
                }
                
                // Calculate average per hour (assuming 8-hour workday)
                const avgPerHour = Math.round((todayData.total_items_24h || 0) / 8);
                
                // Determine tier based on productivity score
                let tier = 'C';
                if (todayData.productivity_score >= 80) {
                    tier = 'A';
                } else if (todayData.productivity_score >= 60) {
                    tier = 'B';
                }
                
                metrics.push({
                    email: trimmedEmail,
                    name: `${todayData.first_name} ${todayData.last_name}`,
                    initials: `${todayData.first_name.charAt(0)}${todayData.last_name.charAt(0)}`,
                    todayItems: todayData.total_items_24h || 0,
                    avg3Days: avg3Days,
                    avgPerHour: avgPerHour,
                    tier: tier
                });
            } else {
                // Picker not found in today's data
                metrics.push({
                    email: trimmedEmail,
                    name: 'Usuario no encontrado',
                    initials: '?',
                    todayItems: 0,
                    avg3Days: 0,
                    avgPerHour: 0,
                    tier: 'C'
                });
            }
        });
        
        setCustomMetrics(metrics);
    };

    // Calculate team metrics from available data
    const calculateTeamMetrics = () => {
        if (!pickerStatsData?.picking_productivities) return;
        
        const pickers = pickerStatsData.picking_productivities;
        const totalPickers = pickers.length;
        
        if (totalPickers === 0) return;
        
        // Calculate averages
        const totalItemsToday = pickers.reduce((sum, picker) => sum + (picker.total_items_24h || 0), 0);
        const avgItemsToday = Math.round(totalItemsToday / totalPickers);
        
        const totalProductivity = pickers.reduce((sum, picker) => sum + (picker.productivity_score || 0), 0);
        const avgProductivity = Math.round(totalProductivity / totalPickers);
        
        // Simulate 3-day average (for now, using today's data * 0.9 as approximation)
        const avgItems3Days = Math.round(avgItemsToday * 0.9);
        
        // Create detailed picker information
        const pickerDetails = pickers.map(picker => {
            let status = 'needs_improvement';
            if (picker.productivity_score >= 80) {
                status = 'excellent';
            } else if (picker.productivity_score >= 60) {
                status = 'good';
            }
            
                            // Calculate average per hour (assuming 8-hour workday)
                const avgPerHour = Math.round((picker.total_items_24h || 0) / 8);
                
                return {
                    email: picker.email,
                    name: `${picker.first_name} ${picker.last_name}`,
                    initials: `${picker.first_name.charAt(0)}${picker.last_name.charAt(0)}`,
                    todayItems: picker.total_items_24h || 0,
                    avg3Days: Math.round((picker.total_items_24h || 0) * 0.9),
                    avgPerHour: avgPerHour,
                    status: status
                };
        });
        
        setTeamMetrics({
            totalPickers,
            avgItemsToday,
            avgItems3Days,
            avgProductivity,
            pickerDetails
        });
    };

    if (isLoading) {
        return (
            <PageView>
                <PageTopBar>
                    <div className="text-lg font-semibold">{t('picking_productivities.title')}</div>
                </PageTopBar>
                <div className="flex justify-center items-center py-20">
                    <Loader show={true} />
                </div>
            </PageView>
        );
    }

    // Show error state if there's an error
    if (hasError) {
        return (
            <PageView>
                <PageTopBar>
                    <div className="text-lg font-semibold">{t('picking_productivities.title')}</div>
                </PageTopBar>
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="text-red-600 mb-4">{t('picking_productivities.error')}</div>
                    <Button onClick={() => window.location.reload()}>
                        {t('picking_productivities.retry')}
                    </Button>
                </div>
            </PageView>
        );
    }

    // If no data is available yet, show a loading state
    if (!hasPickerData) {
        return (
            <PageView>
                <PageTopBar>
                    <div className="text-lg font-semibold">{t('picking_productivities.title')}</div>
                </PageTopBar>
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="text-gray-600 mb-4">Waiting for data to load...</div>
                    <div className="text-sm text-gray-500">This may take a few seconds</div>
                </div>
            </PageView>
        );
    }

    return (
        <PageView>
            <PageTopBar>
                <div>
                    <div className="text-2xl font-bold text-gray-900">{t('picking_productivities.title')}</div>
                    <div className="text-sm text-gray-600 mt-1">{t('picking_productivities.subtitle')}</div>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        type="secondary"
                        onClick={handleRefresh}
                        className="flex items-center space-x-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>Refresh Data</span>
                    </Button>
                </div>
            </PageTopBar>

            {/* View Mode Toggle */}
            <div className="mb-8 flex justify-center">
                <div className="bg-gray-100 rounded-lg p-1 flex space-x-1">
                    <Button
                        type={viewMode === VIEW_MODES.DAY ? 'primary' : 'secondary'}
                        onClick={() => setViewMode(VIEW_MODES.DAY)}
                        dense
                        className="px-6"
                    >
                        {t('picking_productivities.view_by_day')}
                    </Button>
                    <Button
                        type={viewMode === VIEW_MODES.HOUR ? 'primary' : 'secondary'}
                        onClick={() => setViewMode(VIEW_MODES.HOUR)}
                        dense
                        className="px-6"
                    >
                        {t('picking_productivities.view_by_hour')}
                    </Button>
                    <Button
                        type={viewMode === VIEW_MODES.INTERVAL ? 'primary' : 'secondary'}
                        onClick={() => setViewMode(VIEW_MODES.INTERVAL)}
                        dense
                        className="px-6"
                    >
                        {t('picking_productivities.view_by_10min')}
                    </Button>
                </div>
            </div>

            {/* General Insights Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-600">{t('picking_productivities.active_pickers_today')}</div>
                            <div className="text-3xl font-bold text-blue-600">
                                {insightsData?.active_pickers_today || 0}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-600">{t('picking_productivities.active_pickers_last_hour')}</div>
                            <div className="text-3xl font-bold text-green-600">
                                {insightsData?.active_pickers_last_hour || 0}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-600">Total Items (24h)</div>
                            <div className="text-3xl font-bold text-purple-600">
                                {pickerStatsData?.picking_productivities?.reduce((sum, picker) => sum + picker.total_items_24h, 0) || 0}
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Heatmap Table */}
            <div className="bg-white rounded-xl shadow-lg mb-8 border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">📊 Productivity Heatmap</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        {viewMode === VIEW_MODES.DAY && 'Daily performance across all pickers'}
                        {viewMode === VIEW_MODES.HOUR && `Hourly performance across all pickers${warehouse_id === '232' ? ' (BR/SP Timezone)' : ''}`}
                        {viewMode === VIEW_MODES.INTERVAL && `10-minute interval performance across all pickers${warehouse_id === '232' ? ' (BR/SP Timezone)' : ''}`}
                    </p>
                </div>
                
                
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                                    Picker
                                </th>
                                {(() => {
                                    const intervals = getTimeIntervals();
                                    return intervals.map((interval, index) => {
                                        const hasLeftBorder = shouldHaveLeftBorder(interval, intervals, index);
                                        return (
                                            <th key={index} className={`px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${hasLeftBorder ? 'border-l-2 border-gray-300' : ''}`}>
                                                {formatHourDayDisplay(interval)}
                                            </th>
                                        );
                                    });
                                })()}

                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {getHeatmapData().map((picker, pickerIndex) => (
                                <tr key={picker.email} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-8 w-8">
                                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                                    <span className="text-xs font-bold text-white">
                                                        {picker.first_name.charAt(0)}{picker.last_name.charAt(0)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {picker.first_name} {picker.last_name}
                                                </div>
                                                <div className="text-xs text-gray-500">{picker.email}</div>
                                                <div className="text-xs text-blue-600 font-medium">
                                                    {calculateWorkingDays(picker.created_at)} working days
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {(() => {
                                        const intervals = getTimeIntervals();
                                        return intervals.map((interval, intervalIndex) => {
                                            const value = getPickerValueForInterval(picker, interval);
                                            const cellColor = getCellColor(value);
                                            const hasLeftBorder = shouldHaveLeftBorder(interval, intervals, intervalIndex);
                                            
                                            return (
                                                <td key={intervalIndex} className={`px-3 py-2 text-center ${hasLeftBorder ? 'border-l-2 border-gray-300' : ''}`}>
                                                    <div className={`inline-flex items-center justify-center w-12 h-8 rounded text-xs font-medium ${cellColor}`}>
                                                        {value}
                                                    </div>
                                                </td>
                                            );
                                        });
                                    })()}

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Color Legend */}
            <div className="mb-8 flex justify-center">
                <div className="bg-white rounded-lg shadow border border-gray-200 px-6 py-3">
                    <div className="flex items-center space-x-6 text-sm">
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
                            <span className="text-gray-700">
                                {viewMode === VIEW_MODES.DAY && '≥2200 items/day'}
                                {viewMode === VIEW_MODES.HOUR && '≥220 items/hour'}
                                {viewMode === VIEW_MODES.INTERVAL && '≥22 items/10min'}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
                            <span className="text-gray-700">
                                {viewMode === VIEW_MODES.DAY && '1000-2199 items/day'}
                                {viewMode === VIEW_MODES.HOUR && '100-219 items/hour'}
                                {viewMode === VIEW_MODES.INTERVAL && '10-21 items/10min'}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
                            <span className="text-gray-700">
                                {viewMode === VIEW_MODES.DAY && '<1000 items/day'}
                                {viewMode === VIEW_MODES.HOUR && '<100 items/hour'}
                                {viewMode === VIEW_MODES.INTERVAL && '<10 items/10min'}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 bg-gray-100 rounded"></div>
                            <span className="text-gray-500">No activity</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Statistics */}
            {pickerStatsData?.picking_productivities && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {pickerStatsData.picking_productivities.filter(picker => picker.productivity_score >= 80).length}
                            </div>
                            <div className="text-sm text-gray-600">High Performers (≥80%)</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {Math.round((pickerStatsData.picking_productivities.filter(picker => picker.productivity_score >= 80).length / pickerStatsData.picking_productivities.length) * 100)}% of total
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                                {pickerStatsData.picking_productivities.filter(picker => picker.productivity_score >= 60 && picker.productivity_score < 80).length}
                            </div>
                            <div className="text-sm text-gray-600">Good Performers (60-79%)</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {Math.round((pickerStatsData.picking_productivities.filter(picker => picker.productivity_score >= 60 && picker.productivity_score < 80).length / pickerStatsData.picking_productivities.length) * 100)}% of total
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">
                                {pickerStatsData.picking_productivities.filter(picker => picker.productivity_score < 60).length}
                            </div>
                            <div className="text-sm text-gray-600">Needs Improvement (&lt;60%)</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {Math.round((pickerStatsData.picking_productivities.filter(picker => picker.productivity_score < 60).length / pickerStatsData.picking_productivities.length) * 100)}% of total
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Metrics by Email */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">📊 Obtener Métricas por Email</h3>
                    <p className="text-sm text-gray-600 mt-1">Pega una lista de emails para obtener métricas de picking</p>
                </div>
                <div className="p-6">
                    <div className="mb-4">
                        <label htmlFor="emailList" className="block text-sm font-medium text-gray-700 mb-2">
                            Lista de Emails (uno por línea)
                        </label>
                        <textarea
                            id="emailList"
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="usuario1@ejemplo.com&#10;usuario2@ejemplo.com&#10;usuario3@ejemplo.com"
                            value={emailList}
                            onChange={(e) => setEmailList(e.target.value)}
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={handleGetMetrics}
                            disabled={!emailList.trim()}
                            className="px-6"
                        >
                            Obtener Métricas
                        </Button>
                    </div>
                </div>
                
                {/* Metrics Results */}
                {customMetrics.length > 0 && (
                    <div className="px-6 pb-6">
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Resultados de Métricas</h4>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Picker
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Items Hoy
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Promedio 3 Días
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Promedio por Hora
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tier
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {customMetrics.map((metric, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                                                <span className="text-sm font-bold text-white">
                                                                    {metric.initials}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-semibold text-gray-900">
                                                                {metric.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500">{metric.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                    {metric.todayItems}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {metric.avg3Days.toFixed(1)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {metric.avgPerHour.toFixed(1)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                                        metric.tier === 'A' ? 'bg-green-100 text-green-800' :
                                                        metric.tier === 'B' ? 'bg-yellow-100 text-yellow-800' :
                                                        metric.tier === 'C' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {metric.tier}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {customMetrics.map((metric, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                                                <span className="text-sm font-bold text-white">
                                                                    {metric.initials}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-semibold text-gray-900">
                                                                {metric.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500">{metric.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                    {metric.todayItems}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {metric.avg3Days.toFixed(1)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                                        metric.productivity >= 80 ? 'bg-green-100 text-green-800' :
                                                        metric.productivity >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {metric.productivity.toFixed(1)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Team Metrics Summary */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">👥 Métricas del Equipo</h3>
                    <p className="text-sm text-gray-600 mt-1">Resumen de rendimiento del equipo completo</p>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {teamMetrics.totalPickers}
                            </div>
                            <div className="text-sm text-gray-600">Total Pickers</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                                {teamMetrics.avgItemsToday}
                            </div>
                            <div className="text-sm text-gray-600">Promedio Items Hoy</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">
                                {teamMetrics.avgItems3Days}
                            </div>
                            <div className="text-sm text-gray-600">Promedio 3 Días</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">
                                {teamMetrics.avgProductivity}
                            </div>
                            <div className="text-sm text-gray-600">Productividad Promedio</div>
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Detalle por Picker</h4>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Picker
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Items Hoy
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Promedio 3 Días
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Promedio por Hora
                                        </th>

                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Estado
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {teamMetrics.pickerDetails.map((picker, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                                            <span className="text-sm font-bold text-white">
                                                                {picker.initials}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {picker.name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{picker.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                {picker.todayItems}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {picker.avg3Days.toFixed(1)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {picker.avgPerHour.toFixed(1)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                                    picker.status === 'excellent' ? 'bg-green-100 text-green-800' :
                                                    picker.status === 'good' ? 'bg-yellow-100 text-yellow-800' :
                                                    picker.status === 'needs_improvement' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {picker.status === 'excellent' ? 'Excelente' :
                                                     picker.status === 'good' ? 'Bueno' :
                                                     picker.status === 'needs_improvement' ? 'Necesita Mejora' :
                                                     'Sin Datos'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </PageView>
    );
};
